import { z } from 'zod';
import { RecordIdSchema } from './identifiers';
import { LocalizedLabelSchema } from './localized';
import { MoneySchema } from './money';
import { BusinessDateSchema, InstantSchema } from './time';

/**
 * Customer portal — `PORTAL-CUSTOMER-001` (BD-70, authorized by the owner on 2026-10-15).
 *
 * A customer is a separate principal: their own portal account, sessions and token audience; never a
 * staff account. Access starts only from an invitation a staff member issues for an existing customer
 * record — there is no self-registration and no way to claim a record. The activation and reset links
 * are single-use, expire, and are handed over by staff (no e-mail or SMS provider is needed); their
 * tokens never reach a log.
 *
 * Everything the portal returns is read for the signed-in customer's own record only, through
 * portal-specific shapes that carry no staff notes, no internal identifiers of other parties and no
 * financial identifiers. Nothing is paid online.
 */

export const PORTAL_ACCOUNT_STATES = ['invited', 'active', 'suspended'] as const;
export const PortalAccountStateSchema = z.enum(PORTAL_ACCOUNT_STATES);

export const PORTAL_CHANNELS = ['whatsapp', 'phone', 'email', 'none'] as const;

export const PortalPreferencesSchema = z.strictObject({
  language: z.enum(['ar', 'en']),
  /** How the customer prefers to be reminded and contacted; staff contact them manually. */
  reminderChannel: z.enum(PORTAL_CHANNELS),
});
export type PortalPreferences = z.infer<typeof PortalPreferencesSchema>;

/** What staff see of a customer's portal access. */
export const PortalAccessSchema = z.strictObject({
  customerId: RecordIdSchema,
  state: PortalAccountStateSchema.optional(),
  loginIdentifier: z.string().optional(),
  invitedAt: InstantSchema.optional(),
  activatedAt: InstantSchema.optional(),
  lastSignInAt: InstantSchema.optional(),
  preferences: PortalPreferencesSchema.optional(),
});
export type PortalAccess = z.infer<typeof PortalAccessSchema>;

export const PortalInviteSchema = z.strictObject({
  /** The customer's e-mail or mobile number, as they will type it to sign in. */
  loginIdentifier: z.string().trim().min(5).max(200),
});

/** A one-time link to hand to the customer: shown once, never stored in clear, never logged. */
export const PortalLinkSchema = z.strictObject({
  url: z.string(),
  expiresAt: InstantSchema,
});
export type PortalLink = z.infer<typeof PortalLinkSchema>;

export const PortalAccessActionSchema = z.strictObject({
  reason: z.string().trim().min(3).max(500),
});

export const PortalSetPasswordSchema = z.strictObject({
  token: z.string().min(20).max(200),
  password: z.string().min(12).max(200),
});

export const PortalLoginSchema = z.strictObject({
  identifier: z.string().trim().min(3).max(200),
  password: z.string().min(1).max(200),
});

export const PortalSessionSchema = z.strictObject({
  accessToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
});

export const PortalMeSchema = z.strictObject({
  name: z.string(),
  preferences: PortalPreferencesSchema,
});

export const PortalContractSchema = z.strictObject({
  contractId: RecordIdSchema,
  contractNumber: z.string(),
  unitCode: z.string().optional(),
  projectName: LocalizedLabelSchema.optional(),
  state: z.string(),
  totalPrice: MoneySchema,
  paid: MoneySchema,
  outstanding: MoneySchema,
  overdue: MoneySchema,
  contractedOn: BusinessDateSchema,
});
export type PortalContract = z.infer<typeof PortalContractSchema>;

export const PortalInstallmentSchema = z.strictObject({
  sequence: z.number().int(),
  kind: z.string(),
  dueOn: BusinessDateSchema,
  amount: MoneySchema,
  paid: MoneySchema,
  remaining: MoneySchema,
  state: z.string(),
});

export const PortalReceiptSchema = z.strictObject({
  receiptNumber: z.string(),
  contractNumber: z.string().optional(),
  receivedOn: BusinessDateSchema,
  amount: MoneySchema,
  method: z.string(),
  reversed: z.boolean(),
});

export const PortalDocumentSchema = z.strictObject({
  issueId: RecordIdSchema,
  type: z.string(),
  businessReference: z.string(),
  locale: z.enum(['ar', 'en']),
  version: z.number().int(),
  issuedAt: InstantSchema,
});

export const PortalHandoverSchema = z.strictObject({
  number: z.string(),
  contractNumber: z.string(),
  unitCode: z.string(),
  state: z.string(),
  checklist: z.array(z.strictObject({ title: z.string(), done: z.boolean() })),
  appointments: z.array(
    z.strictObject({
      kind: z.string(),
      at: InstantSchema,
      state: z.string(),
    }),
  ),
  openDefects: z.number().int().nonnegative(),
  deliveredOn: BusinessDateSchema.optional(),
});

export const PortalTicketSchema = z.strictObject({
  number: z.string(),
  kind: z.string(),
  subject: z.string(),
  state: z.string(),
  createdAt: InstantSchema,
  /** The resolution text is the staff's answer to the customer; internal notes are never shown. */
  resolution: z.string().optional(),
});

export const PortalTicketInputSchema = z.strictObject({
  kind: z.enum(['complaint', 'maintenance', 'inquiry']),
  subject: z.string().trim().min(3).max(200),
  description: z.string().trim().min(5).max(4000),
  contractId: RecordIdSchema.optional(),
  idempotencyKey: z.string().trim().min(8).max(200),
});

export const PORTAL_AUDIT_ACTIONS = {
  invited: 'portal.account.invited',
  resetIssued: 'portal.account.resetIssued',
  activated: 'portal.account.activated',
  passwordReset: 'portal.account.passwordReset',
  suspended: 'portal.account.suspended',
  reactivated: 'portal.account.reactivated',
  signedIn: 'portal.session.signedIn',
  signInFailed: 'portal.session.signInFailed',
  preferencesChanged: 'portal.account.preferencesChanged',
} as const;
